import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { GroupedVirtuosoHandle } from 'react-virtuoso';
import { PageSection } from 'src/components/common/PageSection';
import { useContent } from 'src/contexts/ContentContext';
import { SelectionMode } from 'src/contexts/MultiSelectionContext';
import { vars } from 'src/core/themes';
import { Stack } from 'src/core/ui';
import { useGetEventFullInfo } from 'src/hooks/useGetEventFullInfo';
import { createInventoryEventUrl } from 'src/navigations/Routes/InventoryEvent/InventoryEvent.utils';
import { ChevronDownIcon, ChevronUpIcon } from 'src/svgs/Viagogo';
import { MobileEventTile } from 'src/tiles/EventTile';
import { EventTileListingMetrics } from 'src/tiles/EventTile/MobileEventTile.components';
import { ContentId } from 'src/utils/constants/contentId';
import { getQuantityNumberFormat } from 'src/utils/format';
import { Event, ListingMetrics } from 'src/WebApiController';

import * as styles from './EventGroupedList.css';

export const EventTilesGroupHeader = ({
  index,
  groupName,
  groupCount,
  virtuosoHandler,
  collapsed,
  updateCollapse,
}: {
  index: number;
  groupName: string;
  groupCount: number;
  virtuosoHandler?: GroupedVirtuosoHandle | null;
  collapsed: boolean;
  updateCollapse: (collapsed: boolean) => void;
}) => {
  const eventText = useContent(ContentId.Event);
  const eventsText = useContent(ContentId.Events);

  const eventCountText = useMemo(() => {
    if (groupCount === 1) {
      return `${groupCount} ${eventText}`;
    }
    return `${getQuantityNumberFormat().format(groupCount)} ${eventsText}`;
  }, [eventText, eventsText, groupCount]);

  return (
    <PageSection.Header
      className={styles.virtuosoGroupHeader}
      onClick={() => {
        virtuosoHandler?.scrollToIndex({ groupIndex: index });
        updateCollapse(!collapsed);
      }}
      action={
        collapsed ? (
          <ChevronDownIcon size={vars.iconSize.s} />
        ) : (
          <ChevronUpIcon size={vars.iconSize.s} />
        )
      }
    >
      <Stack direction="column" justifyContent="center">
        <span>{groupName}</span>
        <span className={styles.virtuosoGroupHeaderSubtitle}>
          {eventCountText}
        </span>
      </Stack>
    </PageSection.Header>
  );
};

export const EventTilesGroupItem = ({
  event,
  metrics,
  selectionMode,
}: {
  event: Event;
  metrics?: ListingMetrics;
  selectionMode?: SelectionMode;
}) => {
  const navigate = useNavigate();
  const { venue, performer } = useGetEventFullInfo(event);

  const onEventClick = useCallback(() => {
    const eventUrl = createInventoryEventUrl(event.virtId, true);
    navigate(eventUrl);
  }, [event.virtId, navigate]);

  return (
    <div className={styles.virtuosoGroupItemContainer}>
      <MobileEventTile
        event={event}
        venue={venue}
        performer={performer}
        metrics={metrics && <EventTileListingMetrics metrics={metrics} />}
        selectionMode={selectionMode}
        onClick={onEventClick}
      />
    </div>
  );
};
